import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  createDiscountCode,
  deactivateDiscountCode,
  listDiscountCodes,
  listServices,
  updateDiscountCode,
} from '@/lib/api';
import type {
  CreateDiscountCodeInput,
  DiscountCode,
  DiscountCodeKind,
  ServiceCatalogueItem,
  UpdateDiscountCodeInput,
} from '@/lib/types';
import { Pencil, Plus, Sparkles, Ticket, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

function majorToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function valueDisplay(code: DiscountCode, currency: string): string {
  if (code.kind === 'pct') return `-${code.value}%`;
  return `-${currencySymbol(currency)}${minorToMajor(code.value)}`;
}

const RANDOM_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function generateRandomCode(): string {
  const bytes = new Uint32Array(8);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < 8; i++) {
    const b = bytes[i];
    if (b === undefined) continue;
    out += RANDOM_CHARSET.charAt(b % RANDOM_CHARSET.length);
  }
  return out;
}

function parseServiceIds(json: string | null): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}

function formatExpiry(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (Number.isNaN(d.getTime())) return iso;
  if (days < 0) return `expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'expires today';
  return `expires in ${days}d`;
}

// ---------------------------------------------------------------------------
// Editor drawer
// ---------------------------------------------------------------------------

interface EditorFormState {
  code: string;
  kind: DiscountCodeKind;
  /** Stored as text — major-currency for fixed_minor, plain percent for pct. */
  valueText: string;
  selectedServiceIds: string[];
  expiresAt: string; // YYYY-MM-DD or ''
  maxUsesText: string;
  maxUsesPerCustomerText: string;
  firstTimeOnly: boolean;
  active: boolean;
}

function emptyForm(): EditorFormState {
  return {
    code: '',
    kind: 'pct',
    valueText: '',
    selectedServiceIds: [],
    expiresAt: '',
    maxUsesText: '',
    maxUsesPerCustomerText: '1',
    firstTimeOnly: false,
    active: true,
  };
}

function formFromCode(code: DiscountCode): EditorFormState {
  return {
    code: code.code,
    kind: code.kind,
    valueText: code.kind === 'pct' ? String(code.value) : minorToMajor(code.value),
    selectedServiceIds: parseServiceIds(code.serviceIdsJson),
    expiresAt: code.expiresAt ? code.expiresAt.slice(0, 10) : '',
    maxUsesText: code.maxUses === null ? '' : String(code.maxUses),
    maxUsesPerCustomerText: String(code.maxUsesPerCustomer),
    firstTimeOnly: code.firstTimeOnly === 1,
    active: code.active === 1,
  };
}

function buildInputFromForm(
  form: EditorFormState,
  currentKind: DiscountCodeKind | null,
): { ok: true; value: number } | { ok: false; error: string } {
  const text = form.valueText.trim();
  if (text === '') return { ok: false, error: 'Value is required' };
  if (form.kind === 'pct') {
    const v = Number(text);
    if (!Number.isFinite(v)) return { ok: false, error: 'Percentage must be a number' };
    if (!Number.isInteger(v)) return { ok: false, error: 'Percentage must be an integer' };
    if (v <= 0 || v > 100) return { ok: false, error: 'Percentage must be 1..100' };
    return { ok: true, value: v };
  }
  // fixed_minor — interpret input as major currency
  const minor = majorToMinor(text);
  if (minor === null) return { ok: false, error: 'Amount must be a number' };
  if (minor <= 0) return { ok: false, error: 'Amount must be positive' };
  // currentKind is unused but kept for forward-compat (e.g. server-rejected
  // kind transitions). Mark consumed to satisfy strict noUnusedParameters.
  void currentKind;
  return { ok: true, value: minor };
}

interface EditorDrawerProps {
  services: ServiceCatalogueItem[];
  existing: DiscountCode | null; // null = create
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}

function EditorDrawer({ services, existing, currency, onClose, onSaved }: EditorDrawerProps) {
  const [form, setForm] = useState<EditorFormState>(() =>
    existing ? formFromCode(existing) : emptyForm(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[] | null>(null);

  function toggleService(id: string) {
    setForm((prev) => {
      const has = prev.selectedServiceIds.includes(id);
      return {
        ...prev,
        selectedServiceIds: has
          ? prev.selectedServiceIds.filter((x) => x !== id)
          : [...prev.selectedServiceIds, id],
      };
    });
  }

  function handleRandomCode() {
    setForm((prev) => ({ ...prev, code: generateRandomCode() }));
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    setServerIssues(null);

    const codeTrimmed = form.code.trim().toUpperCase();
    if (codeTrimmed.length === 0) {
      setError('Code is required');
      setBusy(false);
      return;
    }
    const valueResult = buildInputFromForm(form, existing?.kind ?? null);
    if (!valueResult.ok) {
      setError(valueResult.error);
      setBusy(false);
      return;
    }
    const maxUses =
      form.maxUsesText.trim() === ''
        ? null
        : Number.isInteger(Number(form.maxUsesText)) && Number(form.maxUsesText) > 0
          ? Number(form.maxUsesText)
          : null;
    if (form.maxUsesText.trim() !== '' && maxUses === null) {
      setError('Max uses must be a positive integer');
      setBusy(false);
      return;
    }
    const maxUsesPerCustomer = Number(form.maxUsesPerCustomerText);
    if (!Number.isInteger(maxUsesPerCustomer) || maxUsesPerCustomer < 0) {
      setError('Max uses per customer must be a non-negative integer');
      setBusy(false);
      return;
    }
    const expiresAt = form.expiresAt.trim() === '' ? null : `${form.expiresAt}T23:59:59.000Z`;
    const serviceIdsJson =
      form.selectedServiceIds.length === 0 ? null : JSON.stringify(form.selectedServiceIds);

    try {
      if (existing) {
        const patch: UpdateDiscountCodeInput = {
          code: codeTrimmed,
          kind: form.kind,
          value: valueResult.value,
          serviceIdsJson,
          expiresAt,
          maxUses,
          maxUsesPerCustomer,
          firstTimeOnly: form.firstTimeOnly,
          active: form.active,
        };
        await updateDiscountCode(existing.id, patch);
      } else {
        const input: CreateDiscountCodeInput = {
          code: codeTrimmed,
          kind: form.kind,
          value: valueResult.value,
          serviceIdsJson,
          expiresAt,
          maxUses,
          maxUsesPerCustomer,
          firstTimeOnly: form.firstTimeOnly,
          active: form.active,
        };
        await createDiscountCode(input);
      }
      onSaved();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const issues = (err.body as { issues?: unknown } | null)?.issues;
        if (Array.isArray(issues) && issues.every((s) => typeof s === 'string')) {
          setServerIssues(issues as string[]);
        } else {
          setError(err.code);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      maxWidth="max-w-md"
      aria-label={existing ? 'Edit discount code' : 'New discount code'}
    >
      <div className="flex flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">
            {existing ? 'Edit discount code' : 'New discount code'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Code */}
          <div>
            <label
              htmlFor="dc-code"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Code
            </label>
            <div className="flex gap-2">
              <input
                id="dc-code"
                type="text"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="SUMMER10"
                maxLength={64}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="button"
                onClick={handleRandomCode}
                className="flex items-center gap-1 rounded-md border border-border px-2 py-2 text-xs text-muted-foreground hover:bg-secondary press"
                title="Generate random code"
                aria-label="Generate random code"
              >
                <Sparkles className="size-3.5" strokeWidth={1.75} />
                Random
              </button>
            </div>
          </div>

          {/* Kind + value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="dc-kind"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Kind
              </label>
              <select
                id="dc-kind"
                value={form.kind}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    kind: e.target.value as DiscountCodeKind,
                    valueText: '',
                  }))
                }
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="pct">Percentage</option>
                <option value="fixed_minor">Fixed amount</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="dc-value"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Value{form.kind === 'fixed_minor' ? ` (${currencySymbol(currency)})` : ' (%)'}
              </label>
              <input
                id="dc-value"
                type="number"
                step={form.kind === 'pct' ? '1' : '0.01'}
                min={form.kind === 'pct' ? 1 : 0.01}
                max={form.kind === 'pct' ? 100 : undefined}
                value={form.valueText}
                onChange={(e) => setForm((p) => ({ ...p, valueText: e.target.value }))}
                placeholder={form.kind === 'pct' ? '10' : '5.00'}
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          {/* Services scope */}
          {services.length > 0 && (
            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Applies to services (empty = all)
              </p>
              <div className="flex flex-wrap gap-2 rounded-md border border-border bg-surface p-2">
                {services.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.selectedServiceIds.includes(s.id)}
                      onChange={() => toggleService(s.id)}
                      className="rounded border-border"
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Expires + max uses */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="dc-expires"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Expires (optional)
              </label>
              <input
                id="dc-expires"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label
                htmlFor="dc-max-uses"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Max uses (optional)
              </label>
              <input
                id="dc-max-uses"
                type="number"
                min={1}
                value={form.maxUsesText}
                onChange={(e) => setForm((p) => ({ ...p, maxUsesText: e.target.value }))}
                placeholder="unlimited"
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          {/* Max uses per customer */}
          <div>
            <label
              htmlFor="dc-max-per-customer"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Max uses per customer
            </label>
            <input
              id="dc-max-per-customer"
              type="number"
              min={0}
              value={form.maxUsesPerCustomerText}
              onChange={(e) => setForm((p) => ({ ...p, maxUsesPerCustomerText: e.target.value }))}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Flags */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.firstTimeOnly}
                onChange={(e) => setForm((p) => ({ ...p, firstTimeOnly: e.target.checked }))}
                className="rounded border-border"
              />
              First-time customers only
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                className="rounded border-border"
              />
              Active
            </label>
          </div>

          {/* Errors */}
          {error !== null && <p className="text-sm text-destructive">{error}</p>}
          {serverIssues !== null && (
            <ul className="space-y-1 text-sm text-destructive">
              {serverIssues.map((iss) => (
                <li key={iss}>• {iss}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save code'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Deactivate confirm dialog
// ---------------------------------------------------------------------------

interface DeactivateConfirmProps {
  code: DiscountCode;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DeactivateConfirm({ code, onCancel, onConfirmed }: DeactivateConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await deactivateDiscountCode(code.id);
      onConfirmed();
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label="Confirm deactivate discount code">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Deactivate "{code.code}"?</h2>
        <p className="text-sm text-muted-foreground">
          The code will stop accepting new redemptions. Past redemptions remain on the books.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; code: DiscountCode }
  | { kind: 'deactivate'; code: DiscountCode };

export function DiscountsSettings() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currency = account?.currency ?? 'GBP';

  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const res = await listDiscountCodes();
      setCodes(res.codes);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const svcs = await listServices();
        setServices(svcs);
      } catch {
        // Ignore — page still works without services list.
      }
    })();
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage discount codes
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Discount codes</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Customer-facing promo codes. Codes are case-insensitive on lookup; first-time-only and
            per-customer caps are enforced server-side.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          New code
        </button>
      </header>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : codes.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Ticket className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">No discount codes configured.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a code to offer customers a percentage or fixed-amount discount.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            New code
          </button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="grid grid-cols-[1.5fr_6rem_1fr_8rem_8rem_7rem_auto] gap-3 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>Code</span>
            <span>Kind</span>
            <span>Value</span>
            <span>Uses</span>
            <span>Expires</span>
            <span>Status</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {codes.map((code) => (
              <div
                key={code.id}
                className="grid grid-cols-[1.5fr_6rem_1fr_8rem_8rem_7rem_auto] items-center gap-3 px-6 py-3.5 text-sm"
              >
                <span className="font-medium uppercase tracking-wider">{code.code}</span>
                <span className="inline-flex w-fit items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-foreground ring-hairline">
                  {code.kind === 'pct' ? 'Percentage' : 'Fixed'}
                </span>
                <span className="num text-foreground">{valueDisplay(code, currency)}</span>
                <span className="num text-muted-foreground">
                  {code.usesCount}
                  {code.maxUses !== null ? ` / ${code.maxUses}` : ''}
                </span>
                <span className="text-muted-foreground">{formatExpiry(code.expiresAt)}</span>
                <span>
                  {code.active === 1 ? (
                    <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
                      Inactive
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setModal({ kind: 'edit', code })}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
                    title="Edit code"
                  >
                    <Pencil className="size-3.5" strokeWidth={1.75} />
                  </button>
                  {code.active === 1 && (
                    <button
                      type="button"
                      onClick={() => setModal({ kind: 'deactivate', code })}
                      className="rounded-md p-1 text-muted-foreground hover:text-destructive press"
                      title="Deactivate code"
                    >
                      <X className="size-3.5" strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {modal.kind === 'add' && (
        <EditorDrawer
          services={services}
          existing={null}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'edit' && (
        <EditorDrawer
          services={services}
          existing={modal.code}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'deactivate' && (
        <DeactivateConfirm
          code={modal.code}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
